import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { slotRefKey, type SlotRef } from "@heraion/shared";

import { SelectMark } from "../primitives/SelectMark";
import { webTapFloor, type UiTheme } from "../theme";

// «Also apply to:» under the rule sheet's sentence (#351, brief §5.4 «Una regola
// su più righe»): the same exercise on other days, then the other exercises,
// each a checkbox row, none ticked at the start. A long group shows its first
// rows and a «Show all». The rule is the sheet's: what is ticked only grows
// the list of rows it is written to.

const SHOWN = 4;

type Group = { title: string; refs: readonly SlotRef[] };

export function RuleAlsoApply({
  theme,
  sameExercise,
  others,
  ticked,
  onToggle,
  nameOf,
  dayOf,
}: {
  theme: UiTheme;
  sameExercise: readonly SlotRef[];
  others: readonly SlotRef[];
  ticked: ReadonlySet<string>;
  onToggle: (ref: SlotRef) => void;
  nameOf: (ref: SlotRef) => string;
  dayOf: (day: number) => string;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  const [all, setAll] = useState(false);
  const groups: Group[] = [
    { title: t("editorV3.rule.alsoSame", { defaultValue: "Same exercise, other days" }), refs: sameExercise },
    { title: t("editorV3.rule.alsoOthers", { defaultValue: "Other exercises" }), refs: others },
  ].filter((g) => g.refs.length > 0);
  if (groups.length === 0) return null;
  const long = groups.some((g) => g.refs.length > SHOWN);
  return (
    <View style={local.box}>
      <Text accessibilityRole="header" style={local.title}>
        {t("editorV3.rule.alsoTitle", { defaultValue: "Also apply to:" })}
      </Text>
      {groups.map((g) => (
        <View key={g.title} style={local.group}>
          <Text style={local.groupTitle}>{g.title}</Text>
          {(all ? g.refs : g.refs.slice(0, SHOWN)).map((r) => {
            const on = ticked.has(slotRefKey(r));
            const name = nameOf(r);
            const day = dayOf(r.day);
            return (
              <Pressable
                key={slotRefKey(r)}
                accessibilityRole="checkbox"
                accessibilityLabel={`${name}, ${day}`}
                accessibilityState={{ checked: on }}
                // RNW drops accessibilityState: the DOM attribute says it.
                aria-checked={on}
                onPress={() => onToggle(r)}
                style={local.row}
              >
                <SelectMark theme={theme} checked={on} />
                <View style={local.text}>
                  <Text style={local.name}>{name}</Text>
                  <Text style={local.day}>{day}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      ))}
      {long ? (
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: all }}
          aria-expanded={all}
          onPress={() => setAll((v) => !v)}
          style={local.more}
        >
          <Text style={local.moreText}>
            {all
              ? t("editorV3.rule.alsoShowLess", { defaultValue: "Show fewer" })
              : t("editorV3.rule.alsoShowAll", { defaultValue: "Show all" })}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    box: { gap: s.s2 },
    title: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    group: { gap: 0 },
    groupTitle: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
    row: { flexDirection: "row", alignItems: "center", gap: s.s3, minHeight: webTapFloor ?? 44 },
    text: { flex: 1 },
    name: { color: p.ink, fontSize: ty.sizes.base },
    day: { color: p.ink3, fontSize: ty.sizes.sm },
    more: { minHeight: webTapFloor ?? 44, justifyContent: "center" },
    moreText: { color: p.brand500, fontSize: ty.sizes.sm, fontWeight: "700" },
  });
}
